import { useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { allReviews, reviewStats } from "@/mocks/reviews";
import { SHOW_REVIEWS } from "@/config/features";

const serviceFilters = ["Tous", "DPE", "Pack Vente", "Pack Location", "Amiante", "Électricité", "Loi Carrez"];

export default function AvisPage() {
  const [filter, setFilter] = useState("Tous");

  const filtered = filter === "Tous" ? allReviews : allReviews.filter((r) => r.service === filter);

  // Feature flag : masquage temporaire des avis tant que la fiche GMB n'est pas active
  if (!SHOW_REVIEWS) {
    return (
      <>
        <title>Avis clients — Bientôt disponible | Up Diag</title>
        <meta name="description" content="Notre page avis clients sera disponible très prochainement. En attendant, découvrez nos services de diagnostic immobilier à Paris et en IDF." />
        <meta name="robots" content="noindex,follow" />
        <TopBar />
        <Header />
        <main>
          <section className="min-h-[60vh] flex items-center bg-gradient-to-br from-[#F8FAFC] to-white py-20">
            <div className="max-w-2xl mx-auto px-6 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#F97316]/10 flex items-center justify-center">
                <i className="ri-star-smile-line text-4xl text-[#F97316]"></i>
              </div>
              <h1
                className="text-3xl md:text-4xl font-extrabold text-[#0F3B5F] mb-4"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                Nos avis clients arrivent bientôt
              </h1>
              <p className="text-gray-600 leading-relaxed mb-8">
                Nous collectons actuellement les retours de nos clients via notre fiche Google My
                Business. Cette page affichera très prochainement les témoignages vérifiés de nos clients
                Paris et Île-de-France.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/diagnostics"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all"
                >
                  <i className="ri-file-list-3-line"></i>
                  Voir nos diagnostics
                </Link>
                <Link
                  to="/devis"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0F3B5F] text-[#0F3B5F] rounded-xl font-bold hover:bg-[#0F3B5F] hover:text-white transition-all"
                >
                  Obtenir un devis
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <title>Avis Clients Up Diag — 4.9/5 sur 127 Avis Google | Diagnostic Immobilier Paris</title>
      <meta name="description" content="Découvrez les avis de nos clients sur nos diagnostics immobiliers à Paris et en Île-de-France. Note 4.9/5 sur 127 avis Google vérifiés." />
      <TopBar />
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F3B5F] to-[#0a2d4a] py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/10 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Avis clients
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
              Ils nous font confiance
            </h1>
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="flex gap-1">
                {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F59E0B] text-2xl"></i>)}
              </div>
              <span className="text-4xl font-extrabold text-white" style={{ fontFamily: "Barlow, sans-serif" }}>4.9/5</span>
            </div>
            <p className="text-white/70">Basé sur {reviewStats.total} avis Google vérifiés</p>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-white py-14 border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              {/* Score global */}
              <div className="text-center">
                <div className="text-7xl font-extrabold text-[#0F3B5F] mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>{reviewStats.average}</div>
                <div className="flex justify-center gap-1 mb-2">
                  {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F59E0B] text-2xl"></i>)}
                </div>
                <p className="text-gray-500 text-sm">{reviewStats.total} avis Google vérifiés</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <i className="ri-google-fill text-[#F97316] text-xl"></i>
                  <span className="text-sm font-semibold text-gray-600">Google Reviews</span>
                </div>
              </div>
              {/* Breakdown */}
              <div className="flex flex-col gap-3">
                {reviewStats.breakdown.map((row) => (
                  <div key={row.stars} className="flex items-center gap-3">
                    <div className="flex gap-0.5 w-20 flex-shrink-0">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className={`ri-star-fill text-sm ${s <= row.stars ? "text-[#F59E0B]" : "text-gray-200"}`}></i>
                      ))}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                      <div className="h-full bg-[#F59E0B] rounded-full transition-all duration-700" style={{ width: `${row.pct}%` }}></div>
                    </div>
                    <span className="text-sm text-gray-500 w-8 text-right">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Filters + Reviews */}
        <section className="bg-[#F8FAFC] py-16">
          <div className="max-w-6xl mx-auto px-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-10">
              {serviceFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    filter === f ? "bg-[#F97316] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-[#F97316] hover:text-[#F97316]"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((review) => (
                <div key={review.id} className="bg-white rounded-2xl p-6 border-l-4 border-[#F97316] hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className={`ri-star-fill text-sm ${s <= review.rating ? "text-[#F59E0B]" : "text-gray-200"}`}></i>
                      ))}
                    </div>
                    <span className="text-xs bg-orange-100 text-[#F97316] font-bold px-2 py-0.5 rounded-full">{review.service}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed italic mb-5">"{review.text}"</p>
                  <div className="border-t border-gray-100 pt-4 flex items-center gap-3">
                    <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#0F3B5F] text-sm">{review.name}</div>
                      <div className="text-xs text-gray-400 truncate">{review.type} — {review.location}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-google-fill text-[#F97316]"></i>
                        Vérifié
                      </div>
                      <span className="text-xs text-gray-400">{review.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-star-line text-4xl mb-3 block"></i>
                <p>Aucun avis pour ce filtre.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Rejoignez nos clients satisfaits
            </h2>
            <p className="text-white/75 mb-8">Devis gratuit en 2 minutes — Intervention sous 48h</p>
            <Link to="/devis" className="inline-flex items-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap">
              <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
